import { GET_COINFLIP_GAME } from '@/api/query'
import { RulesButton } from '@/components/atoms/RulesButton/RulesButton'
import { useSession } from '@/context/SessionProvider'
import { useAppToast } from '@/hooks/useAppToast'
import { Grid, Box, Button, Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
import { useMutation } from '@tanstack/react-query'
import { GameRules } from 'types/types'
import { useCoinflipStore } from './CoinflipProvider'
import { GameStatus, GAME_MODE } from './types'
import { useApolloClient } from '@apollo/client'
import { delay } from '@/helpers/delay'
import { PayableAmount } from './PayableAmount'
import { SOUNDS, useAudio } from '@/context/AudioProvider'
import { memo, useEffect } from 'react'
import { COINFLIP_CONTRACT_ADDRESS } from '@/constants/constants'
import { useBalanceStore } from '@/context/UserBalance'
import { getTokenByDenom } from '@/services/token-service'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'
import { CurrencyDropdown } from '@/components/molecules/CurrencyDropdown/CurrencyDropdown'
import { StyledInput2 } from '@/components/atoms/StyledInput/StyledInput'
import { AmountButtons } from '@/components/atoms/AmountButtons/AmountButtons'
import { useTranslation } from 'next-i18next/pages'
import { GameInfo } from '@/components/atoms/GameInfo/GameInfo'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { currenciesSettings } from './CoinflipProvider'

export const Form = memo(() => {
  const {
    view: { status },
    setValues,
    form,
  } = useCoinflipStore()
  const denom = useBalanceStore((state) => state.denom)
  const { t } = useTranslation()

  useEffect(() => {
    useCoinflipStore.getState().setDenom(denom)
  }, [denom])

  const isDisabled = status === GameStatus.ACTIVE
  const maxValue = form.fields.maxValue
  const amount = form.fields.amount
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const usdcToFiat = useFiatCurrencyStore((s) => s.usdcToFiat)
  const getSymbol = useFiatCurrencyStore((s) => s.getSymbol)
  const isUsdc = getTokenByDenom(denom)?.currency === SupportedCurrencies.USDC
  const maxInfoText =
    isUsdc && !isUsd
      ? t('common.max') + ' ' + usdcToFiat(maxValue).toFixed(2) + ' ' + getSymbol()
      : t('common.max') + ' ' + maxValue.toFixed(2)

  const rtp = useCoinflipStore((state) => Number(state.gameConfig.rtp))

  return (
    <Grid
      gap={7}
      gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
      bg={'ninja.600'}
      p={{ base: 4, md: 5 }}
      rounded={'2xl'}
    >
      <Box>
        <FormTitle isDisabled={isDisabled} />
        <Box mb={2}>
          <StyledInput2
            clearable={true}
            title={t('common.amount')}
            placeholder={t('common.enter-amount')}
            icon={getTokenByDenom(denom).icon()}
            setValue={(value) => {
              setValues({ amount: { value, setDirty: true } })
            }}
            errors={form.errors}
            isDirty={form.isDirty}
            amount={amount}
            infoText={maxInfoText}
            isDisabled={isDisabled}
            denom={denom}
          />
        </Box>
        <AmountButtons
          maxValue={maxValue}
          denom={denom}
          setAmount={(value) => setValues({ amount: { value, setDirty: true } })}
          amount={amount}
          disabled={isDisabled}
        />
        <Box mt={2} hideBelow="md">
          <GameInfo
            rtp={`${rtp * 100}%`}
            maxWin={
              form.fields.mode === GAME_MODE.MULTIPLY ? (
                <FiatOrCryptoAmount
                  value={
                    currenciesSettings.maxWin[GAME_MODE.MULTIPLY][getTokenByDenom(denom)?.currency]?.toString() || '200'
                  }
                  denom={denom}
                  iconHidden={true}
                  wrapperStyles={{ fontSize: 'xs', display: 'inline' }}
                  iconSize={3}
                />
              ) : undefined
            }
          />
        </Box>
      </Box>
      <Box>
        <GameMode isDisabled={isDisabled} />
        <PayoutButton />
      </Box>
      <Box hideFrom="md" gridColumn="1" transform="translateY(-20px)" mb={-5}>
        <GameInfo
          rtp={`${rtp * 100}%`}
          maxWin={
            form.fields.mode === GAME_MODE.MULTIPLY ? (
              <FiatOrCryptoAmount
                value={
                  currenciesSettings.maxWin[GAME_MODE.MULTIPLY][getTokenByDenom(denom)?.currency]?.toString() || '200'
                }
                denom={denom}
                iconHidden={true}
                wrapperStyles={{ fontSize: 'xs', display: 'inline' }}
                iconSize={3}
              />
            ) : undefined
          }
        />
      </Box>
    </Grid>
  )
})

const PayoutButton = () => {
  const { sendTransaction, account } = useSession()
  const {
    view: { status, isDrawing, roundState: viewRoundState },
    roundState,
    gameId,
    isWithdrawing,
    form,
    setWithdrawing,
    setWithdrawingOff,
    handleEndGame,
    dispatch,
  } = useCoinflipStore()
  const { onError } = useAppToast()
  const appoloClient = useApolloClient()
  const { t } = useTranslation()

  const { play } = useAudio()

  const { mutate } = useMutation({
    mutationFn: async () => {
      setWithdrawing()
      while (true) {
        const game = await appoloClient.query({
          query: GET_COINFLIP_GAME,
          fetchPolicy: 'no-cache',
          variables: {
            id: gameId,
          },
        })

        if (
          game.data?.coinflipGame?.gameRound == game.data.coinflipGame?.processedGameRound &&
          game.data?.coinflipGame?.gameRound == roundState.currentStep
        ) {
          break
        }

        await delay(500)
      }

      const tx = await sendTransaction(
        MsgExecuteContractCompat.fromJSON({
          sender: account?.injectiveAddress!,
          contractAddress: COINFLIP_CONTRACT_ADDRESS,
          funds: [],
          msg: {
            payout: {
              game_id: Number(gameId),
            },
          },
        }),
        { showError: false }
      )

      if (!tx?.txHash) {
        throw new Error()
      }

      play(SOUNDS.PLAYER_WON)

      setWithdrawingOff()
      handleEndGame()
      dispatch()
    },
    onError: () => {
      setWithdrawingOff()
      onError({ custom: { title: t('coinflip.unable-to-withdraw') } })
    },
  })

  if (status === GameStatus.ACTIVE && viewRoundState.currentStep !== 0 && form.fields.mode === GAME_MODE.MULTIPLY) {
    return (
      <Button
        isDisabled={isDrawing}
        isLoading={isWithdrawing}
        onClick={() => mutate()}
        rounded={'2xl'}
        w={'full'}
        h={'56px'}
        gap={3}
        variant={'orange'}
        fontSize={'lg'}
      >
        <Text>{t('common.withdraw')}</Text>
        <PayableAmount />
      </Button>
    )
  }

  return (
    <Button isDisabled={true} rounded={'2xl'} w={'full'} h={'56px'} fontSize={'lg'}>
      {t('coinflip.click-heads-or-tails')}
    </Button>
  )
}

const GameMode = ({ isDisabled }: { isDisabled: boolean }) => {
  const { t } = useTranslation()
  const { form, setValue, clearHistory } = useCoinflipStore()
  const { play } = useAudio()
  const mode = form.fields.mode

  const handleChangeMode = (mode: GAME_MODE) => {
    if (!isDisabled) {
      clearHistory()
      setValue('mode', mode)

      play(SOUNDS.SET_AMOUNT)
    }
  }

  return (
    <Box mb={2}>
      <Flex mb={2} justifyContent={'space-between'}>
        <Text fontWeight={700}>{t('common.game-mode')}</Text>
        <RulesButton
          styles={{
            h: 7,
            rounded: 'lg',
            fontSize: 'xs',
            py: 0,
            px: 4,
          }}
          game={GameRules.COINFLIP}
        />
      </Flex>
      <SimpleGrid
        opacity={isDisabled ? 0.6 : 1}
        gap={1}
        p={1}
        h={10}
        fontSize={'sm'}
        fontWeight={700}
        bg={'ninja.500'}
        rounded={'xl'}
        columns={2}
      >
        <Flex
          onClick={() => handleChangeMode(GAME_MODE.INSTANT)}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          rounded={'lg'}
          bg={mode === GAME_MODE.INSTANT ? '#3D3C70' : 'transparent'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Text color={mode !== GAME_MODE.INSTANT ? 'ninja.300' : 'white'}>{t('coinflip.fast')}</Text>
        </Flex>
        <Flex
          onClick={() => handleChangeMode(GAME_MODE.MULTIPLY)}
          bg={mode === GAME_MODE.MULTIPLY ? '#3D3C70' : 'transparent'}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          rounded={'lg'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Text color={mode !== GAME_MODE.MULTIPLY ? 'ninja.300' : 'white'}>{t('coinflip.multiply')}</Text>
        </Flex>
      </SimpleGrid>
    </Box>
  )
}

const FormTitle = ({ isDisabled }: { isDisabled: boolean }) => {
  const { t } = useTranslation()
  return (
    <Box mb={2}>
      <Flex alignItems={'center'} justifyContent={'space-between'}>
        <Text userSelect={'none'} fontWeight={700}>
          {t('common.your-bet')}
        </Text>
        <Flex alignItems={'center'} gap={2}>
          <CurrencyDropdown isDisabled={isDisabled} />
        </Flex>
      </Flex>
    </Box>
  )
}
